import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { PHOTOS } from '@/lib/photos';

/**
 * Photographic "wet asphalt + police light" backdrop.
 * Combines:
 *  - a real dark empty street photo (deliberately graded)
 *  - blue & red radial bokeh orbs (police-light glow)
 *  - grain + scanline vignette
 *  - subtle parallax on scroll
 * No faces / no vehicles visible.
 */
export default function GlowBackground({ intense = false, variant = 'street' }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1200], [0, -140]);
  const y2 = useTransform(scrollY, [0, 1200], [0, 200]);
  const y3 = useTransform(scrollY, [0, 1200], [0, -60]);
  const bgY = useTransform(scrollY, [0, 900], [0, 60]);

  const image =
    variant === 'water'
      ? PHOTOS.darkWater
      : variant === 'ink'
      ? PHOTOS.inkGlow
      : PHOTOS.wetStreet;

  return (
    <div ref={ref} aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Real photograph — deep dark grade */}
      <motion.div
        style={{ y: bgY, backgroundImage: `url(${image})` }}
        className="absolute inset-0 bg-cover bg-center opacity-[0.55] mix-blend-luminosity"
      />
      {/* Cool blue grade over photo */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,17,31,0.55)_0%,rgba(7,17,31,0.75)_45%,rgba(7,17,31,0.95)_100%)]" />
      {/* Colour cast — blue */}
      <div className="absolute inset-0 mix-blend-screen bg-[radial-gradient(60%_50%_at_25%_60%,rgba(10,132,255,0.28),transparent_70%)]" />
      {/* Colour cast — red */}
      <div className="absolute inset-0 mix-blend-screen bg-[radial-gradient(55%_45%_at_78%_38%,rgba(255,64,64,0.22),transparent_70%)]" />

      {/* Bokeh orbs — Blue (soft, huge) */}
      <motion.div
        style={{ y: y1 }}
        className={`absolute -left-40 top-16 h-[520px] w-[520px] rounded-full glow-blue animate-pulse-glow ${intense ? 'opacity-95' : 'opacity-75'}`}
      />
      {/* Bokeh orbs — Red */}
      <motion.div
        style={{ y: y2 }}
        className={`absolute -right-32 top-40 h-[480px] w-[480px] rounded-full glow-red animate-pulse-glow ${intense ? 'opacity-90' : 'opacity-70'}`}
      />
      {/* Smaller bright cores */}
      <motion.div
        style={{ y: y3 }}
        className="absolute left-[18%] top-[42%] h-6 w-6 rounded-full bg-[#39A8FF] blur-[6px] opacity-90"
      />
      <div className="absolute right-[22%] top-[36%] h-5 w-5 rounded-full bg-[#FF5252] blur-[5px] opacity-90" />
      <div className="absolute left-[32%] top-[62%] h-3 w-3 rounded-full bg-[#39A8FF] blur-[3px] opacity-95" />
      <div className="absolute right-[35%] top-[60%] h-2.5 w-2.5 rounded-full bg-[#FF4040] blur-[3px] opacity-95" />

      {/* Wet floor reflection: mirrored soft light band at bottom */}
      <div className="absolute inset-x-0 bottom-0 h-[38%]">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,17,31,0.6)_0%,rgba(7,17,31,0)_40%,rgba(7,17,31,0)_60%,#07111F_100%)]" />
        <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.06),transparent)]" />
        <div className="absolute inset-x-[10%] top-6 h-24 blur-2xl bg-[radial-gradient(50%_100%_at_50%_0%,rgba(10,132,255,0.35),transparent)] opacity-70" />
        <div className="absolute right-[8%] top-4 h-24 w-1/2 blur-2xl bg-[radial-gradient(50%_100%_at_50%_0%,rgba(255,80,80,0.32),transparent)] opacity-70" />
      </div>

      {/* faint grid overlay */}
      <div className="absolute inset-0 grid-bg opacity-25 mix-blend-overlay" />

      {/* grain */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />
      {/* Final vignette to keep text legible */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(7,17,31,0.6)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(180deg,transparent,#07111F)]" />
    </div>
  );
}
